import React, { useEffect, useRef } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetFootprintingPorkFootprint,
  useKeyedFormList,
  useMutateFootprintingPorkStables,
  useTranslation,
} from "utils";

import {
  ENUM_PORK_AMMONIA_FILTRATION_EFFICIENCY,
  ENUM_PORK_STABLE_TYPE,
  FootprintingPorkStablePayload,
  PORK_AMMONIA_FILTRATION_EFFICIENCY,
  PORK_STABLE_TYPE,
} from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tag,
  TagColour,
  Typography,
} from "components";

type StableEntry = FootprintingPorkStablePayload;

const EFFICIENCY_TAG_COLOUR: Record<string, TagColour> = {
  very_low: "red",
  low: "orange",
  medium: "beige",
  good: "green-300",
  high: "light-green",
  very_high: "yellow",
};

const emptyStable: StableEntry = {
  stableType: null,
  stableSize: null,
  hasAmmoniaFiltration: null,
  ammoniaFiltrationEfficiency: null,
};

type StableTypeFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const PorkStableTypeForm = ({ primaryDataId, readOnly, farmId, year }: StableTypeFormProps) => {
  const { t } = useTranslation("footprintingPork");
  const formRef = useRef<FormInstance>(null);

  const { data: porkFootprint, isLoading } = useGetFootprintingPorkFootprint(farmId, year);
  const keyedStables = useKeyedFormList<StableEntry>(formRef, { isReady: !isLoading });

  const { mutate: mutateStables } = useMutateFootprintingPorkStables(
    farmId,
    year,
    { stables: keyedStables.payload.filter((s) => s.stableType !== null) },
    primaryDataId ?? ""
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        mutateStables();
      }
    },
    [isLoading, mutateStables, readOnly, primaryDataId]
  );

  useEffect(() => {
    if (isLoading) return;

    const footprintStables = porkFootprint?.footprint?.stables ?? [];
    const loaded = footprintStables.map((s) => ({
      stableType: s.stableType,
      stableSize: s.stableSize,
      hasAmmoniaFiltration: s.hasAmmoniaFiltration,
      ammoniaFiltrationEfficiency: s.ammoniaFiltrationEfficiency,
    }));

    // Saved stables are checked straight away; a first-visit blank stable stays neutral
    keyedStables.actions.hydrate(loaded.length > 0 ? loaded : [emptyStable]);
    if (loaded.length > 0) keyedStables.actions.requestRehydrationValidation();
  }, [porkFootprint, isLoading, keyedStables.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-pork-stable-type-form" className="padding-200">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={24}>
          {keyedStables.entries.map((stable, index) => {
            const stableTypeSelectName = `stable-type-select-${index}`;
            const ammoniaEfficiencySelectName = `ammonia-efficiency-select-${index}`;

            return (
              <Flex key={stable.clientKey} vertical gap={24}>
                <Flex justify="space-between" align="center">
                  <Typography variant="h5">{t("porkStableTypeForm.stableNumber", { count: index + 1 })}</Typography>
                  {keyedStables.entries.length > 1 && (
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      dataTestId={`delete-stable-${index}`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => keyedStables.actions.remove(stable.clientKey)}
                    />
                  )}
                </Flex>

                <Select
                  disabled={readOnly}
                  name={`stable-type-select-${stable.clientKey}`}
                  dataTestId={stableTypeSelectName}
                  label={t("porkStableTypeForm.question.stableTypeLabel")}
                  rules={[{ required: true, message: "" }]}
                  value={stable.stableType ? t(`porkStableTypeForm.stableTypeOptions.${stable.stableType}`) : ""}
                  initialValue={stable.stableType ? t(`porkStableTypeForm.stableTypeOptions.${stable.stableType}`) : ""}
                  onChange={(v) => {
                    const match = Object.values(PORK_STABLE_TYPE).find((type) => t(`porkStableTypeForm.stableTypeOptions.${type}`) === v);
                    keyedStables.actions.update(stable.clientKey, { stableType: match as ENUM_PORK_STABLE_TYPE });
                  }}
                  dropdownStyle={{ zIndex: 10010 }}
                >
                  {Object.values(PORK_STABLE_TYPE).map((type) => (
                    <Option
                      key={`${stableTypeSelectName}-${type}`}
                      data-testid={`${stableTypeSelectName}-${type}`}
                      value={t(`porkStableTypeForm.stableTypeOptions.${type}`)}
                    >
                      {t(`porkStableTypeForm.stableTypeOptions.${type}`)}
                    </Option>
                  ))}
                </Select>

                <InputNumber
                  disabled={readOnly}
                  name={`stableSize-${stable.clientKey}`}
                  dataTestId={`footprinting-pork-stable-size-input-${index}`}
                  label={t("porkStableTypeForm.question.stableSizeLabel")}
                  suffix={t("porkStableTypeForm.question.stableSizeSuffix")}
                  value={stable.stableSize}
                  initialValue={stable.stableSize}
                  onChange={(v) => keyedStables.actions.update(stable.clientKey, { stableSize: v })}
                  validateFirst
                  rules={[
                    { required: true, type: "number", message: t("porkStableTypeForm.error.validStableSize") },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                          return Promise.reject(new Error(t("porkStableTypeForm.error.validStableSize")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />

                <Flex align="center" gap={16} justify="space-between">
                  <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
                    {t("porkStableTypeForm.question.ammoniaFiltrationQuestion")}
                    <span className="required-asterisk">*</span>
                  </Typography>
                  <KlimRadioGroup
                    required
                    name={`hasAmmoniaFiltration-${stable.clientKey}`}
                    dataTestId={`footprinting-pork-ammonia-filtration-radio-${index}`}
                    disabled={readOnly}
                    vertical={false}
                    type="tertiary"
                    size="large"
                    onChange={({ target }) => {
                      keyedStables.actions.update(stable.clientKey, {
                        hasAmmoniaFiltration: target.value,
                        ammoniaFiltrationEfficiency: target.value ? stable.ammoniaFiltrationEfficiency : null,
                      });
                    }}
                    value={stable.hasAmmoniaFiltration}
                    defaultValue={stable.hasAmmoniaFiltration}
                    optionType="button"
                    options={[
                      { label: t("porkStableTypeForm.ammoniaFiltrationOptions.yes"), value: true },
                      { label: t("porkStableTypeForm.ammoniaFiltrationOptions.no"), value: false },
                    ]}
                  />
                </Flex>

                {stable.hasAmmoniaFiltration === false && (
                  <AlertBanner
                    type="recommended"
                    iconName="tabler/bulb"
                    iconAlign="start"
                    iconSize={24}
                    isTitleBold
                    headingSize="paragraph-m"
                    title={t("porkStableTypeForm.noAmmoniaFilterBanner.title")}
                    description={t("porkStableTypeForm.noAmmoniaFilterBanner.description")}
                  />
                )}

                {stable.hasAmmoniaFiltration === true && (
                  <Flex vertical gap={4}>
                    <Select
                      disabled={readOnly}
                      name={`ammonia-efficiency-select-${stable.clientKey}`}
                      dataTestId={ammoniaEfficiencySelectName}
                      label={t("porkStableTypeForm.question.ammoniaEfficiencyLabel")}
                      rules={[{ required: true, message: "" }]}
                      value={stable.ammoniaFiltrationEfficiency ? t(`porkStableTypeForm.ammoniaEfficiencyOptions.${stable.ammoniaFiltrationEfficiency}`) : ""}
                      initialValue={
                        stable.ammoniaFiltrationEfficiency ? t(`porkStableTypeForm.ammoniaEfficiencyOptions.${stable.ammoniaFiltrationEfficiency}`) : ""
                      }
                      onChange={(v) => {
                        const match = Object.values(PORK_AMMONIA_FILTRATION_EFFICIENCY).find(
                          (eff) => t(`porkStableTypeForm.ammoniaEfficiencyOptions.${eff}`) === v
                        );
                        keyedStables.actions.update(stable.clientKey, { ammoniaFiltrationEfficiency: match as ENUM_PORK_AMMONIA_FILTRATION_EFFICIENCY });
                      }}
                      dropdownStyle={{ zIndex: 10010 }}
                    >
                      {Object.values(PORK_AMMONIA_FILTRATION_EFFICIENCY).map((eff) => (
                        <Option
                          key={`${ammoniaEfficiencySelectName}-${eff}`}
                          data-testid={`${ammoniaEfficiencySelectName}-${eff}`}
                          value={t(`porkStableTypeForm.ammoniaEfficiencyOptions.${eff}`)}
                        >
                          <Flex justify="start" gap={12} align="center">
                            {t(`porkStableTypeForm.ammoniaEfficiencyOptions.${eff}`)}
                            <Tag text={t(`porkStableTypeForm.ammoniaEfficiencyTags.${eff}`)} colour={EFFICIENCY_TAG_COLOUR[eff]} />
                          </Flex>
                        </Option>
                      ))}
                    </Select>
                    <Typography variant="paragraph-s" color="grey-600">
                      {t("porkStableTypeForm.question.ammoniaEfficiencyHelperText")}
                    </Typography>
                  </Flex>
                )}
              </Flex>
            );
          })}

          <Button type="text" dataTestId="add-stable-button" disabled={readOnly} onClick={() => keyedStables.actions.add(emptyStable)}>
            <Icon iconName="klim/plus" className="icon inline" /> {t("porkStableTypeForm.addStable")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

PorkStableTypeForm.Key = "pork.stableType";
export { PorkStableTypeForm };
